import { useEffect } from 'react'

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { describeCombo, enterScope } from '@/lib/keys'

import { TOOLS } from './tools/reducer'

/**
 * The `?` overlay (§9.3), rendered **from the key map** rather than from a
 * hand-written list: the tool rows come straight out of the tool registry,
 * so a tool can never ship with a shortcut the sheet does not know about.
 */

export interface ShortcutSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const MOD = describeCombo('$mod')

/**
 * Only keys that are really bound (AnnotatorLayout, the kind bar, `useEditorCommands`) — the
 * sheet once promised Tab and `.` / `,` that did nothing. Letters work on any keyboard layout.
 */
const EDIT_KEYS: [string, string][] = [
  ['1 – 9', 'Say what the selected thing is — or, with nothing selected, what you add next (the numbers on the kind list)'],
  ['/', 'Search every kind, or make a new one'],
  [`${MOD} + S`, 'Save'],
  [`${MOD} + Z`, 'Undo (while drawing: take back the last dot)'],
  [`${MOD} + Shift + Z  /  ${MOD} + Y`, 'Redo'],
  ['Delete', 'Remove the selected thing'],
  ['Enter', 'Finish the shape you are drawing'],
  ['Backspace', 'While drawing: take back the last dot'],
  ['Esc', 'Cancel, then clear the selection'],
  ['↑ ↓ in Everything', 'Next / previous thing on the picture'],
  ['Arrows', 'Nudge the selected thing a little (Shift: more)'],
  [`${MOD} + A`, 'Select everything'],
  [`${MOD} + D`, 'Duplicate'],
  ['Alt + click', 'Remove a point'],
]

const VIEW_KEYS: [string, string][] = [
  ['Space + drag', 'Move around the picture'],
  ['Mouse wheel', 'Zoom in / out where the pointer is'],
  [`${MOD} + 0`, 'Fit the whole picture'],
  [`${MOD} + 1`, 'Actual size'],
  ['+  /  −', 'Zoom in / out'],
  ['`', 'Flip between before and after a suggestion'],
  ['E', 'Hide the selected shape'],
  ['Shift + E', 'Show only the selected shape'],
  ['[  /  ]', 'Previous / next picture'],
  [`${MOD} + K`, 'Search for anything'],
  ['?', 'This list'],
]

export function ShortcutSheet({ open, onOpenChange }: ShortcutSheetProps) {
  // While the sheet is open the editor keys (digits, Delete, tool letters) stay quiet.
  useEffect(() => (open ? enterScope('dialog') : undefined), [open])
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Keyboard shortcuts</DialogTitle>
          <DialogDescription>Everything here is also a button somewhere — the keys are just faster.</DialogDescription>
        </DialogHeader>

        <div className="grid gap-6 sm:grid-cols-3">
          <Section title="Tools">
            {TOOLS.map((t) => (
              <Row key={t.id} keys={t.shortcut.toUpperCase()} label={`${t.label} — ${t.hint}`} />
            ))}
          </Section>
          <Section title="Editing">
            {EDIT_KEYS.map(([k, label]) => (
              <Row key={k} keys={k} label={label} />
            ))}
          </Section>
          <Section title="Looking around">
            {VIEW_KEYS.map(([k, label]) => (
              <Row key={k} keys={k} label={label} />
            ))}
          </Section>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-3">{title}</h3>
      <dl className="space-y-1.5">{children}</dl>
    </section>
  )
}

function Row({ keys, label }: { keys: string; label: string }) {
  return (
    <div className="flex gap-2 text-sm">
      <dt className="shrink-0">
        <kbd className="rounded bg-paper-3 px-1.5 py-0.5 font-mono text-xs text-ink">{keys}</kbd>
      </dt>
      <dd className="text-ink-2">{label}</dd>
    </div>
  )
}
